import { useEffect, useEffectEvent, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../../utils/api-client';
import { fetchTelegramLinkToken, fetchWebSession } from '../auth';
import type { TelegramLinkTokenResponse } from '../types';
import { getLocalizedApiErrorMessage } from '../utils/errorHelpers';
import { QrCode } from './QrCode';
import {
  canShowQrCode,
  formatCountdown,
  PRIMARY_BUTTON_CLASS,
  PRIMARY_BUTTON_STYLE,
  Spinner,
} from './telegramDeepLink';
import Icon from '../../miniapp/ui-kit/Icon';

type Phase = 'idle' | 'requesting' | 'waiting' | 'expired' | 'error' | 'unavailable';

const SESSION_POLL_INTERVAL_MS = 3000;

interface TelegramLinkPanelProps {
  /**
   * Called once the web session changes under the panel: `linked` when it now carries a Telegram
   * account (`/start link_…` merged it in the bot), otherwise the session is gone. The caller
   * refreshes the session user, which normally unmounts the panel.
   */
  onSessionChanged: (linked: boolean) => Promise<void>;
}

/**
 * Links Telegram to the signed-in website account with a signed `t.me/<bot>?start=link_…` deep
 * link (button, plus a QR code on desktop), then watches `/api/web/auth/session` until it reports a
 * `telegramId`. Needs no Telegram username.
 */
export default function TelegramLinkPanel({ onSessionChanged }: TelegramLinkPanelProps) {
  const { t, i18n } = useTranslation();
  const [phase, setPhase] = useState<Phase>('idle');
  const [link, setLink] = useState<TelegramLinkTokenResponse | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [now, setNow] = useState(() => Date.now());
  const [canShowQr] = useState(canShowQrCode);
  const mounted = useRef(false);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  async function start(): Promise<void> {
    setPhase('requesting');
    setErrorMessage('');

    try {
      const response = await fetchTelegramLinkToken();
      if (!mounted.current) {
        return;
      }

      setLink(response);
      setNow(Date.now());
      setPhase('waiting');
    } catch (error) {
      if (!mounted.current) {
        return;
      }

      if (error instanceof ApiError && error.code === 'TELEGRAM_BOT_NOT_CONFIGURED') {
        setPhase('unavailable');
        return;
      }

      setErrorMessage(
        getLocalizedApiErrorMessage(error, t, {
          exists: i18n.exists.bind(i18n),
          fallbackKey: 'website.telegramLink.error',
        }),
      );
      setPhase('error');
    }
  }

  function cancel(): void {
    // The token is stateless: nothing to revoke, it simply expires.
    setLink(null);
    setPhase('idle');
  }

  /** Reads the session once; returns whether polling should continue. */
  const checkSession = useEffectEvent(async (): Promise<boolean> => {
    try {
      const session = await fetchWebSession();
      if (session.authenticated && !session.data?.telegramId) {
        return true;
      }

      await onSessionChanged(Boolean(session.authenticated && session.data?.telegramId));
      return false;
    } catch {
      // Transient network error: keep waiting until the link expires.
      return true;
    }
  });

  const expireLocally = useEffectEvent(() => {
    setPhase('expired');
  });

  // Session polling: chained setTimeout (never overlapping), paused while the tab is hidden, and an
  // immediate check when the tab becomes visible again (returning from Telegram).
  useEffect(() => {
    if (phase !== 'waiting' || !link) {
      return;
    }

    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    let checking = false;
    let disposed = false;
    const expiresAt = Date.parse(link.expiresAt);

    const poll = async () => {
      if (disposed || checking || document.visibilityState === 'hidden') {
        return;
      }
      if (Date.now() >= expiresAt) {
        expireLocally();
        return;
      }

      checking = true;
      const keepPolling = await checkSession();
      checking = false;
      if (keepPolling && !disposed) {
        timeoutId = setTimeout(() => void poll(), SESSION_POLL_INTERVAL_MS);
      }
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        void poll();
      }
    };

    timeoutId = setTimeout(() => void poll(), SESSION_POLL_INTERVAL_MS);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      disposed = true;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [phase, link]);

  // Countdown tick while waiting.
  useEffect(() => {
    if (phase !== 'waiting') {
      return;
    }

    const intervalId = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(intervalId);
  }, [phase]);

  if (phase === 'unavailable') {
    return null;
  }

  if (phase === 'waiting' && link) {
    const remainingMs = Date.parse(link.expiresAt) - now;

    return (
      <section className="grid gap-4 rounded-2xl bg-secondary p-5">
        <div className="flex flex-col gap-5 md:flex-row md:items-center">
          {canShowQr ? (
            <div className="hidden shrink-0 flex-col items-center gap-2 md:flex">
              <QrCode value={link.deepLinkUrl} label={t('website.telegramLink.scanLabel')} />
              <span className="text-xs text-text-secondary">{t('website.telegramLink.scan')}</span>
            </div>
          ) : null}

          <div className="grid flex-1 gap-3">
            <a
              href={link.deepLinkUrl}
              target="_blank"
              rel="noreferrer"
              className={PRIMARY_BUTTON_CLASS}
              style={PRIMARY_BUTTON_STYLE}
            >
              <Icon name="send" className="text-[20px]" />
              {t('website.telegramLink.open')}
            </a>
            <p
              role="status"
              aria-live="polite"
              className="flex items-center gap-2 text-sm text-text"
            >
              <Spinner />
              {t('website.telegramLink.pending')}
            </p>
            <p className="text-xs text-text-secondary">
              {t('website.telegramLink.linkExpires', { countdown: formatCountdown(remainingMs) })}
            </p>
            <button
              type="button"
              className="justify-self-start text-xs font-medium text-text-secondary hover:underline"
              onClick={cancel}
            >
              {t('website.telegramLink.cancel')}
            </button>
          </div>
        </div>
      </section>
    );
  }

  const terminalMessage =
    phase === 'expired' ? t('website.telegramLink.expired') : phase === 'error' ? errorMessage : '';

  return (
    <section className="grid gap-3">
      {terminalMessage ? (
        <p
          role={phase === 'error' ? 'alert' : 'status'}
          className={
            phase === 'error'
              ? 'rounded-2xl bg-error-container px-4 py-3 text-sm text-on-error-container'
              : 'rounded-2xl bg-secondary px-4 py-3 text-sm text-text'
          }
        >
          {terminalMessage}
        </p>
      ) : null}

      <button
        type="button"
        className={PRIMARY_BUTTON_CLASS}
        style={PRIMARY_BUTTON_STYLE}
        disabled={phase === 'requesting'}
        onClick={() => void start()}
      >
        {phase === 'requesting' ? (
          <Spinner />
        ) : (
          <Icon name="send" className="text-[20px]" />
        )}
        {terminalMessage ? t('website.telegramLink.retry') : t('website.telegramLink.start')}
      </button>
      {!terminalMessage ? (
        <p className="text-center text-xs text-text-secondary">
          {t('website.telegramLink.helper')}
        </p>
      ) : null}
    </section>
  );
}
